// FaceWrap.js
import React, { useRef, useState } from "react";
import axios from "axios";
import {
  ReactCompareSlider,
  ReactCompareSliderImage,
} from "react-compare-slider";
import {
  Container,
  Alert,
  Button,
  Typography,
  Popover,
  Box,
  useMediaQuery,
  Chip,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import GlowPaper from "./GlowPaper";
import styles from "./FaceWrap.module.css";
import {
  Download,
  AddPhotoAlternate,
  Compare,
  Info,
} from "@mui/icons-material";
import TopHeader from "../TopHeader";
import ImageUploadSection from "./ImageUploadSection";

const FaceWrap = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [processedImage, setProcessedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [showUploadSection, setShowUploadSection] = useState(true);
  const fileInputRef = useRef(null);
  const userToken =
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiI2Nzk5ZjAyZjQ1MGM4N2M0ZGY5ODcyNTQiLCJpYXQiOjE3MzgzMTk4MTN9.iJiZBqUHPQuFxYxE5qN1ovkbIdTcMNau6ZD2bL08ya4";

  const handleImageUpload = async () => {
    if (!selectedFile || !description) {
      setError("Please select an image and enter a description");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("image", selectedFile);
    formData.append("description", description);

    try {
      const response = await axios.post(
        "https://easyramini.onrender.com/user/faceWrap/generate",
        formData,
        {
          headers: {
            "x-user-token": userToken,
          },
        }
      );

      if (response.data.status) {
        setProcessedImage(response.data.data.img.url);
        setError("");
        setShowUploadSection(false);
      } else {
        setError(response.data.message || "Face wrapping failed");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Server error. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const imageUrl = processedImage;
      const filename = imageUrl.split("/").pop();

      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  const gradientButtonStyle = {
    background:
      "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)",
    color: "#fff",
    fontWeight: 600,
    padding: "12px 24px",
    borderRadius: "12px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(99, 102, 241, 0.3)",
    },
  };

  return (
    <div className={styles.mainContainer}>
      <TopHeader />
      <Container className={styles.container} maxWidth="xl">
        <AnimatePresence>
          {showUploadSection && (
            <GlowPaper elevation={3}>
              <ImageUploadSection
                loading={loading}
                onImageUpload={handleImageUpload}
                onFileSelected={(file) => setSelectedFile(file)}
                fileInputRef={fileInputRef}
                selectedFile={selectedFile}
                description={description}
                setDescription={setDescription}
              />
            </GlowPaper>
          )}
        </AnimatePresence>

        {processedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className={styles.resultContainer}
          >
            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{
                  mb: 4,
                  fontWeight: 800,
                  background: "linear-gradient(45deg, #2196f3, #90caf9)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.03em",
                  fontSize: isMobile ? "2rem" : "2.5rem",
                }}
              >
                Face Wrap Result Preview
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignContent: "center",
                  alignItems: "center",
                }}
                elevation={3}
              >
                <Box
                  sx={{
                    position: "relative",
                    height: { xs: "50vh", md: "70vh" },
                    width: "max-content",
                    minHeight: "400px",
                    borderRadius: "24px",
                    overflow: "hidden",
                    boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
                  }}
                >
                  <div className={styles.imageLabelBefore}>Before</div>
                  <div className={styles.imageLabelAfter}>After</div>

                  <ReactCompareSlider
                    style={{ height: "100%", width: "100%" }}
                    itemOne={
                      <ReactCompareSliderImage
                        src={URL.createObjectURL(selectedFile)}
                        alt="Original image"
                        style={{ objectFit: "contain" }}
                      />
                    }
                    itemTwo={
                      <ReactCompareSliderImage
                        src={processedImage}
                        alt="Face wrapped image"
                        style={{ objectFit: "contain" }}
                      />
                    }
                    onlyHandleDraggable
                    handle={
                      <div className={styles.sliderHandle}>
                        <Compare sx={{ color: "#fff", fontSize: "2rem" }} />
                      </div>
                    }
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  justifyContent: "center",
                  mt: 4,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowUploadSection(true);
                    setProcessedImage(null);
                    setSelectedFile(null);
                  }}
                  sx={gradientButtonStyle}
                  startIcon={<AddPhotoAlternate sx={{ fontSize: "1.2rem" }} />}
                >
                  Upload New Image
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    ...gradientButtonStyle,
                    background:
                      "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)",
                  }}
                  onClick={(e) => setExportAnchorEl(e.currentTarget)}
                  startIcon={<Download sx={{ fontSize: "1.2rem" }} />}
                >
                  Export Result
                </Button>
              </Box>
            </Box>
          </motion.div>
        )}

        <Popover
          open={Boolean(exportAnchorEl)}
          anchorEl={exportAnchorEl}
          onClose={() => setExportAnchorEl(null)}
          PaperProps={{
            sx: {
              background: "rgba(15, 23, 42, 0.97)",
              backdropFilter: "blur(24px)",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.2)",
              boxShadow: "0 24px 48px rgba(0,0,0,0.4)",
              minWidth: "300px",
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: "white", fontWeight: 600 }}
            >
              Download Options
            </Typography>
            <Button
              fullWidth
              variant="contained"
              sx={gradientButtonStyle}
              onClick={handleDownload}
            >
              Download Full Resolution
            </Button>
          </Box>
        </Popover>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              style={{
                position: "fixed",
                top: "20px",
                right: "20px",
                zIndex: 9999,
              }}
            >
              <Alert
                severity="error"
                sx={{
                  background: "rgba(239, 68, 68, 0.15)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  color: "#fff",
                }}
              >
                {error}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </div>
  );
};

export default FaceWrap;
